import React, { useState } from 'react';
import { Box, Container, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import RefreshIcon from '@mui/icons-material/Refresh';
import BarChartIcon from '@mui/icons-material/BarChart';
import DashboardLayout from '../components/common/DashboardLayout/DashboardLayout';
import AdminStatsCards from '../components/admin/AdminStatsCards';
import PendingJobsTable from '../components/admin/PendingJobsTable';
import JobReviewModal from '../components/admin/JobReviewModal';
import VideoPreviewModal from '../components/dubbing/VideoPreviewModal';
import useAdminJobs from '../hooks/useAdminJobs';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { showSuccess, showError } = useToast();

  const [selectedJob, setSelectedJob]     = useState(null);
  const [reviewModalOpen, setReviewOpen]  = useState(false);
  const [previewModalOpen, setPreviewOpen] = useState(false);
  const [previewJob, setPreviewJob]       = useState(null);

  const {
    pendingJobs, adminStats, loading, error,
    loadAdminData, approveJob, rejectJob,
    getJobDetails, hasPendingJobs, pendingCount,
  } = useAdminJobs();

  React.useEffect(() => {
    if (!loading && !isAdmin()) {
      navigate('/dubbing');
      showError('Access denied. Admin privileges required.');
    }
  }, [loading, isAdmin, navigate, showError]);

  if (loading && !user) {
    return (
      <DashboardLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', color: 'rgba(255,255,255,0.3)', fontSize: '14px' }}>
            Loading…
          </Typography>
        </Box>
      </DashboardLayout>
    );
  }

  if (!isAdmin()) return null;

  const handleRefresh = () => { loadAdminData(); showSuccess('Dashboard refreshed'); };

  const handleViewDetails = async (job) => {
    try {
      const details = await getJobDetails(job.id);
      setSelectedJob(details || job);
      setReviewOpen(true);
    } catch { showError('Failed to load job details'); }
  };

  const handleApprove = async (job, notes = '') => {
    try { await approveJob(job.id, notes); setReviewOpen(false); } catch {}
  };

  const handleReject = async (job, notes) => {
    try { await rejectJob(job.id, notes); setReviewOpen(false); } catch {}
  };

  return (
    <DashboardLayout>
      <Container maxWidth={false} sx={{ py: { xs: 3, md: 4 }, px: { xs: 2, md: 4 }, maxWidth: '1300px', margin: '0 auto' }}>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 4 }}>
          <Box>
            <Typography sx={{
              fontFamily: 'Inter, sans-serif', fontWeight: 800,
              fontSize: { xs: '2rem', md: '2.6rem' },
              letterSpacing: '-0.03em', color: '#FFFFFF', lineHeight: 1.1, mb: 0.75,
            }}>
              Admin Dashboard
            </Typography>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.95rem', color: 'rgba(255,255,255,0.45)', fontWeight: 400 }}>
              Content moderation and job approval system.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, flexShrink: 0 }}>
            <Button
              onClick={() => navigate('/admin/analytics')}
              startIcon={<BarChartIcon sx={{ fontSize: 16 }} />}
              sx={{
                backgroundColor: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.7)',
                fontFamily: 'Inter, sans-serif', fontWeight: 500,
                fontSize: '14px', textTransform: 'none',
                borderRadius: '8px', px: 2, py: 1.2,
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)', color: '#FFFFFF' },
              }}
            >
              User Analytics
            </Button>

            <Button
              onClick={handleRefresh}
              startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
              sx={{
                backgroundColor: '#FFFFFF', color: '#000000',
                fontFamily: 'Inter, sans-serif', fontWeight: 700,
                fontSize: '14px', textTransform: 'none',
                borderRadius: '8px', px: 2, py: 1.2,
                '&:hover': { backgroundColor: '#F0F0F0' },
              }}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {/* Error */}
        {error && (
          <Box sx={{ mb: 3, px: 2, py: 1.5, borderRadius: '8px', backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#EF4444' }}>{error}</Typography>
          </Box>
        )}

        {/* Stats */}
        <AdminStatsCards stats={adminStats} loading={loading} />

        {/* Pending Jobs */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: '1.1rem', color: '#FFFFFF' }}>
              Pending Video Reviews
            </Typography>
            {pendingCount > 0 && (
              <Box sx={{
                px: 1, py: 0.25, borderRadius: '5px',
                backgroundColor: 'rgba(245,158,11,0.15)',
                fontFamily: 'Inter, sans-serif', fontSize: '12px', fontWeight: 700,
                color: '#F59E0B',
              }}>
                {pendingCount} waiting
              </Box>
            )}
          </Box>

          {hasPendingJobs && (
            <Box sx={{ mb: 2, px: 2, py: 1.25, borderRadius: '8px', backgroundColor: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#F59E0B', flexShrink: 0 }} />
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#F59E0B' }}>
                {pendingCount} video{pendingCount > 1 ? 's' : ''} awaiting review — please approve or reject before processing begins.
              </Typography>
            </Box>
          )}

          <PendingJobsTable
            jobs={pendingJobs}
            loading={loading}
            onApprove={(job) => approveJob(job.id, 'Quick approval')}
            onReject={handleViewDetails}
            onViewDetails={handleViewDetails}
            onVideoPreview={(job) => { setPreviewJob(job); setPreviewOpen(true); }}
          />
        </Box>

        {/* System status */}
        {adminStats && (
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', pt: 2, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <StatusStat label="Processed today" value={(adminStats.total_approved_today || 0) + (adminStats.total_rejected_today || 0)} />
            <StatusStat label="Processing queue" value={`${adminStats.total_processing_jobs || 0} jobs`} />
          </Box>
        )}
      </Container>

      <JobReviewModal
        open={reviewModalOpen}
        onClose={() => setReviewOpen(false)}
        job={selectedJob}
        onApprove={handleApprove}
        onReject={handleReject}
      />
      <VideoPreviewModal
        open={previewModalOpen}
        onClose={() => setPreviewOpen(false)}
        job={previewJob}
      />
    </DashboardLayout>
  );
};

const StatusStat = ({ label, value }) => (
  <Box>
    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', color: 'rgba(255,255,255,0.3)', mb: 0.25, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
      {label}
    </Typography>
    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '15px', fontWeight: 700, color: 'rgba(255,255,255,0.7)' }}>
      {value}
    </Typography>
  </Box>
);

export default AdminDashboardPage;
